import { createContext, useContext, ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Org-level permission resolution for custom roles (Phase 1).
 *
 * Mirrors FeatureContext: one cached RPC call (`get_my_permissions`) returns the
 * set of permission keys granted to the current user's role, and `can(key)` does
 * an O(1) lookup. super_admin resolves to all keys server-side.
 *
 * IMPORTANT: this is a CLIENT-SIDE gate (UX — hide tabs/actions). It is NOT a
 * security boundary; real per-role data restriction is Phase 2 (RLS via
 * has_permission()). Consumers should fail OPEN while `isLoading` so a slow/failed
 * fetch never locks a legitimate user out of their own app.
 */
interface PermissionContextType {
  permissions: Set<string>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  can: (permissionKey: string) => boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissionContext = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissionContext must be used within a PermissionProvider');
  }
  return context;
};

/** Convenience hook: `const canManageRoles = useCan('action.roles.manage')`. */
export const useCan = (permissionKey: string): boolean =>
  usePermissionContext().can(permissionKey);

/**
 * `useCan` for components that also render outside the provider (shared
 * dialogs, isolated tests): no provider means "not granted" instead of a throw.
 */
export const useCanIfProvided = (permissionKey: string): boolean => {
  const context = useContext(PermissionContext);
  return context ? context.can(permissionKey) : false;
};

/**
 * `useCanIfProvided`, but false while permissions are still loading: for
 * EDIT affordances that must not flash on for someone who lacks the key. It
 * still fails open once a fetch has settled with an error (see `can`).
 */
export const useCanOnceLoaded = (permissionKey: string): boolean => {
  const context = useContext(PermissionContext);
  if (!context || context.isLoading) return false;
  return context.can(permissionKey);
};

/** False while permissions load, and outside the provider: there is no answer yet. */
export const usePermissionsSettled = (): boolean => {
  const context = useContext(PermissionContext);
  return !!context && !context.isLoading;
};

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider = ({ children }: PermissionProviderProps) => {
  const { user, accessScope } = useAuth();

  const { data: permissionKeys, isLoading, error, refetch } = useQuery({
    queryKey: ['my-permissions', user?.id, accessScope],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      const { data, error } = await supabase.rpc('get_my_permissions');
      if (error) {
        // Fail open: on error, return null so consumers can tell "unknown" from
        // "explicitly empty" and avoid hiding the whole app.
        console.error('Permission fetch error:', error);
        return null;
      }
      return (Array.isArray(data) ? data : []) as string[];
    },
    enabled: !!user?.id && !!accessScope,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const permissions = useMemo(
    () => new Set<string>(permissionKeys ?? []),
    [permissionKeys],
  );

  // `resolved` distinguishes "loaded a real (possibly empty) set" from
  // "still loading / errored". Consumers use this to fail open.
  const resolved = !isLoading && permissionKeys != null;

  const can = (permissionKey: string): boolean => {
    if (!resolved) return true; // fail open until we have a definitive answer
    return permissions.has(permissionKey);
  };

  const contextValue: PermissionContextType = {
    permissions,
    // Raw fetch state only. On error the query settles (isLoading=false) and
    // `can()` fails open via `resolved` — so a failed fetch never hangs a route
    // guard or hides the app; it just stops gating.
    isLoading: isLoading || (!!user && !accessScope),
    error: error as Error | null,
    refetch: () => {
      refetch();
    },
    can,
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};
